import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api from "../../../lib/api";

// Order Discount (Sep 2026) — the staff action for a manual discount on
// ONE placed, unpaid order, shown inside the Retail Orders drawer.
//
// The backend (`services/order_discount.py`) is the authority for every
// rule: it computes the discount amount, re-prices tax/tip/total with the
// one totals engine, and refuses once payment collection has begun. This
// panel only collects the staff input and shows what the server returned
// — it never calculates money. `canEdit` below is a courtesy that hides
// the controls on an order that is obviously paid; the server re-checks.

const EDITABLE_PAYMENT_STATUSES = new Set(["", "pending", "failed", "cancelled"]);

function formatMoney(v, currency) {
  const n = Number(v || 0);
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "USD").toUpperCase() }).format(n);
  } catch (_e) {
    return `$${n.toFixed(2)}`;
  }
}

function errorMessage(e, fallback) {
  const det = e?.response?.data?.detail;
  if (det && typeof det === "object" && det.message) return det.message;
  if (typeof det === "string") return det;
  return fallback;
}

export default function OrderDiscountPanel({ order, currency, onChanged }) {
  const detail = order?.order_discount_detail || null;
  const paymentStatus = String(order?.payment_status || "").toLowerCase();
  const canEdit = EDITABLE_PAYMENT_STATUSES.has(paymentStatus);
  const [open, setOpen] = useState(false);
  const [discountType, setDiscountType] = useState(detail?.type || "percent");
  const [value, setValue] = useState(detail?.value || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (!order) return null;
  if (!detail && !canEdit) return null;

  const startEdit = () => {
    setDiscountType(detail?.type || "percent");
    setValue(detail?.value || "");
    setReason("");
    setOpen(true);
  };

  const apply = async () => {
    if (busy) return;
    if (!String(value).trim()) { toast.error("Enter a discount value."); return; }
    if (reason.trim().length < 3) { toast.error("A reason is required."); return; }
    setBusy(true);
    try {
      const { data } = await api.put(`/retail/orders/${order.id}/order-discount`, {
        discount_type: discountType,
        value: String(value).trim(),
        reason: reason.trim(),
      });
      toast.success(`Order discount applied. New total ${formatMoney(data?.totals?.total, currency)}.`);
      setOpen(false);
      onChanged && onChanged(data?.order || null);
    } catch (e) {
      toast.error(errorMessage(e, "Could not apply the discount."));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (busy) return;
    if (reason.trim().length < 3) { toast.error("A reason is required to remove the discount."); return; }
    setBusy(true);
    try {
      const { data } = await api.delete(`/retail/orders/${order.id}/order-discount`, {
        data: { reason: reason.trim() },
      });
      toast.success(`Order discount removed. New total ${formatMoney(data?.totals?.total, currency)}.`);
      setOpen(false);
      onChanged && onChanged(data?.order || null);
    } catch (e) {
      toast.error(errorMessage(e, "Could not remove the discount."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 rounded-md border border-slate-200 p-3 text-sm" data-testid="order-discount-panel">
      {detail ? (
        <div className="text-xs text-slate-600 space-y-0.5" data-testid="order-discount-detail">
          <div>
            <span className="font-semibold text-slate-800">Order Discount: </span>
            {detail.type === "percent" ? `${detail.value}%` : formatMoney(detail.value, currency)}
            {" "}({formatMoney((detail.amount_cents || 0) / 100, currency)} off)
          </div>
          <div>Reason: {detail.reason}</div>
          <div>
            By {detail.updated_by_email || detail.applied_by_email || "staff"}
            {detail.updated_at ? ` · ${new Date(detail.updated_at).toLocaleString()}` : ""}
          </div>
        </div>
      ) : null}

      {canEdit && !open && (
        <button
          type="button"
          onClick={startEdit}
          className="mt-2 h-8 px-3 rounded-md border border-slate-300 text-xs font-medium hover:bg-slate-50"
          data-testid="order-discount-open"
        >
          {detail ? "Change Order Discount" : "Apply Order Discount"}
        </button>
      )}

      {canEdit && open && (
        <div className="mt-2 space-y-2" data-testid="order-discount-form">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setDiscountType("percent")}
              className={"h-8 px-3 rounded-md border text-xs " + (discountType === "percent" ? "bg-slate-800 text-white border-slate-800" : "border-slate-300")}
              data-testid="order-discount-type-percent"
            >
              Percentage
            </button>
            <button
              type="button"
              onClick={() => setDiscountType("fixed")}
              className={"h-8 px-3 rounded-md border text-xs " + (discountType === "fixed" ? "bg-slate-800 text-white border-slate-800" : "border-slate-300")}
              data-testid="order-discount-type-fixed"
            >
              Fixed Amount
            </button>
          </div>
          <input
            type="number"
            min="0"
            step="0.01"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={discountType === "percent" ? "Percent, e.g. 10" : "Amount, e.g. 10.00"}
            className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm"
            data-testid="order-discount-value"
          />
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            placeholder="Reason (required)"
            className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm"
            data-testid="order-discount-reason"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={apply}
              disabled={busy}
              className="h-8 px-3 rounded-md bg-orange-500 text-white text-xs font-semibold disabled:opacity-60 inline-flex items-center gap-1"
              data-testid="order-discount-apply"
            >
              {busy && <Loader2 className="w-3 h-3 animate-spin" />} Apply Discount
            </button>
            {detail && (
              <button
                type="button"
                onClick={remove}
                disabled={busy}
                className="h-8 px-3 rounded-md border border-red-300 text-red-700 text-xs disabled:opacity-60"
                data-testid="order-discount-remove"
              >
                Remove Discount
              </button>
            )}
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={busy}
              className="h-8 px-3 rounded-md border border-slate-300 text-xs"
              data-testid="order-discount-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
